import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase/config';
import {
  collection,
  addDoc,
  query,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';
import './PartyChat.css';

function PartyChat({ partyId, user, onClose }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  // Listen for messages
  useEffect(() => {
    const messagesRef = collection(db, 'puzzleParties', partyId, 'messages');
    const q = query(messagesRef, orderBy('timestamp', 'asc'), limit(100));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const msgs = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setMessages(msgs);
      },
      (err) => {
        // Only log non-permission errors
        if (err.code !== 'permission-denied') {
          console.warn('PartyChat listener error:', err);
        }
      }
    );

    return () => unsubscribe();
  }, [partyId]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      await addDoc(collection(db, 'puzzleParties', partyId, 'messages'), {
        text: newMessage.trim(),
        userId: user.uid,
        userName: user.displayName || user.email.split('@')[0],
        timestamp: serverTimestamp(),
      });
      setNewMessage('');
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Quick messages
  const quickMessages = [
    '🎉 Nice!',
    '💪 Keep going!',
    '🧩 Found it!',
    '😅 This is hard!',
    '🤔 Need help?',
    '☕ Break time!',
  ];

  const sendQuickMessage = (msg) => {
    setNewMessage(msg);
  };

  return (
    <div className="party-chat-overlay">
      <div className="party-chat">
        <div className="chat-header">
          <h3>💬 Team Chat</h3>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="chat-messages">
          {messages.length === 0 ? (
            <div className="no-messages">
              <p>👋 Say hello to your team!</p>
            </div>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.id}
                className={`chat-message ${
                  msg.userId === user.uid ? 'own' : ''
                }`}
              >
                {msg.userId !== user.uid && (
                  <span className="message-sender">{msg.userName}</span>
                )}
                <div className="message-content">
                  <p>{msg.text}</p>
                  <span className="message-time">
                    {formatTime(msg.timestamp)}
                  </span>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Messages */}
        <div className="quick-messages">
          {quickMessages.map((msg, idx) => (
            <button
              key={idx}
              className="quick-msg-btn"
              onClick={() => sendQuickMessage(msg)}
            >
              {msg}
            </button>
          ))}
        </div>

        <form className="chat-input-form" onSubmit={handleSend}>
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            maxLength={500}
          />
          <button type="submit" disabled={!newMessage.trim() || sending}>
            {sending ? '...' : '➤'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default PartyChat;
